/**
 * Gift Selection Utilities
 * Helper functions for session management and gift selection operations
 *
 * CRITICAL TERMINOLOGY:
 * =====================
 * - **entityId**: Business entity identifier (product ID, cart item reference ID)
 *   → This is the actual business object ID (e.g., productId: "12345")
 *
 * - **sessionId**: Unique session identifier = `gift-${entityId}-${context}`
 *   → This is generated from entityId + context for session management
 *   → Format: "gift-12345-pdp" or "gift-12345-cart-abc123"
 *
 * - **entityType**: Type of business entity ('product' | 'cart')
 *   → Determines how entity is treated in business logic
 *
 * - **context**: Usage context (pdp, cart, wishlist, etc.)
 *   → Differentiates multiple sessions for same entity
 *
 * RELATIONSHIP:
 * =============
 * One entityId can have MULTIPLE sessions in different contexts:
 *
 * Product 12345 (entityId)
 *   ├─ Session 1: gift-12345-pdp (PDP page)
 *   ├─ Session 2: gift-12345-cart-abc (Cart item 1)
 *   └─ Session 3: gift-12345-cart-xyz (Cart item 2, same product)
 *
 * Each session has INDEPENDENT gift selections.
 *
 * USAGE:
 * ======
 * ```typescript
 * import { giftSelectionUtils } from './utils/gift-selection-utils';
 *
 * // Generate session ID from entityId and context
 * const productId = '12345';  // This is entityId
 * const sessionId = giftSelectionUtils.generateSessionId(productId, 'pdp');
 * // sessionId: 'gift-12345-pdp'
 *
 * // For cart items, use cartItemReferenceId as entityId
 * const cartItemRef = 'abc123';
 * const sessionId = giftSelectionUtils.generateSessionId(productId, `cart-${cartItemRef}`);
 * // sessionId: 'gift-12345-cart-abc123'
 *
 * // Check session existence
 * if (giftSelectionUtils.hasExistingSession(sessionId)) {
 *   // Session exists
 * }
 *
 * // Extract entityId back from sessionId
 * const extractedEntityId = giftSelectionUtils.extractEntityId(sessionId);
 * // extractedEntityId: '12345'
 *
 * // Get session state
 * const session = giftSelectionUtils.getSessionState(sessionId);
 * // session.entityId: '12345'
 * // session.sessionId: 'gift-12345-pdp'
 * ```
 */
import { giftSelectionStoreAPI } from '../stores/simple-gift-selection-store.public-api';
import { type GiftSelectionSession, type GiftSelectionEntityType } from '../stores/simple-gift-selection.store';
import { type FreeGiftsData } from '../../../types/front.graphql';
import { type SelectedGift } from '../types/gift-selection.types';

/**
 * Gift Selection Utility Functions
 *
 * TERMINOLOGY:
 * - entityId: Business entity identifier (product ID, cart item reference ID)
 * - sessionId: Unique session identifier = `gift-${entityId}-${context}`
 * - entityType: Type of entity ('product' | 'cart')
 * - context: Usage context (pdp, cart, wishlist, etc.)
 *
 * RELATIONSHIP:
 * - One entityId can have MULTIPLE sessions (different contexts)
 * - Each session is uniquely identified by sessionId
 * - SessionId = entityId + context
 */
export const giftSelectionUtils = {
    /**
     * Check if session exists in store
     *
     * @param sessionId - Session identifier
     * @returns True if session exists
     */
    hasExistingSession: (sessionId: string): boolean => {
        return giftSelectionStoreAPI.computed.sessionExists(sessionId);
    },

    /**
     * Create new session with initial configuration
     *
     * @param sessionId - Session identifier
     * @param entityType - Type of entity ('product' or 'cart')
     * @param availableGifts - Available gift rules
     * @param maxTurns - Maximum selection turns (default: 3)
     * @param productQuantity - Product quantity (default: 0)
     * @returns Created session ID
     */
    createNewSession: (
        sessionId: string,
        entityType: GiftSelectionEntityType,
        availableGifts: FreeGiftsData[] = [],
        maxTurns: number = 3,
        productQuantity: number = 0
    ): string => {
        return giftSelectionStoreAPI.actions.createSession(
            sessionId,
            entityType,
            availableGifts,
            maxTurns,
            productQuantity
        );
    },

    /**
     * Destroy session completely
     *
     * @param sessionId - Session identifier
     */
    destroySession: (sessionId: string): void => {
        giftSelectionStoreAPI.actions.deleteSession(sessionId);
    },

    /**
     * Clear selected gifts in session (keeps session alive)
     *
     * @param sessionId - Session identifier
     */
    clearSessionGifts: (sessionId: string): void => {
        giftSelectionStoreAPI.actions.clearSelectedGifts(sessionId);
    },

    /**
     * Get session state (read-only)
     *
     * @param sessionId - Session identifier
     * @returns Session data or undefined
     */
    getSessionState: (sessionId: string): Readonly<GiftSelectionSession> | undefined => {
        return giftSelectionStoreAPI.state.getSessionById(sessionId);
    },

    /**
     * Get selected gifts for session
     *
     * @param sessionId - Session identifier
     * @returns Array of selected gifts
     */
    getSelectedGifts: (sessionId: string): ReadonlyArray<SelectedGift> => {
        return giftSelectionStoreAPI.state.getSelectedGifts(sessionId);
    },

    /**
     * Get remaining product quantity for session
     *
     * @param sessionId - Session identifier
     * @param upToRound - Calculate up to this round (default: 1)
     * @returns Remaining quantity
     */
    getRemainingQuantity: (sessionId: string, upToRound: number = 1): number => {
        return giftSelectionStoreAPI.computed.getRemainingQuantity(sessionId, upToRound);
    },

    /**
     * Update session configuration
     *
     * @param sessionId - Session identifier
     * @param updates - Partial session updates
     */
    updateSession: (sessionId: string, updates: Partial<GiftSelectionSession>): void => {
        giftSelectionStoreAPI.actions.updateSession(sessionId, updates);
    },

    /**
     * Check if session is complete (all turns have gifts)
     *
     * @param sessionId - Session identifier
     * @returns True if session is complete
     */
    isSessionComplete: (sessionId: string): boolean => {
        const session = giftSelectionStoreAPI.state.getSessionById(sessionId);
        if (!session) return false;

        const selectedGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId);
        const maxTurns = session.maxSelectionTurns || 1;

        return selectedGifts.length >= maxTurns;
    },

    /**
     * Destroy all sessions for a specific entity
     *
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     */
    destroyEntitySessions: (entityId: string, entityType: GiftSelectionEntityType): void => {
        giftSelectionStoreAPI.actions.deleteEntitySessions(entityId, entityType);
    },

    /**
     * Get all sessions for debugging
     *
     * @returns All sessions in store
     */
    getAllSessions: (): Readonly<Record<string, GiftSelectionSession>> => {
        return giftSelectionStoreAPI.state.getAllSessions();
    },

    /**
     * Reset all sessions (use with caution - mainly for testing)
     */
    resetAllSessions: (): void => {
        giftSelectionStoreAPI.actions.resetAllSessions();
    },

    /**
     * Check if any gift rule is eligible for a given quantity.
     * A gift is eligible when the quantity satisfies the rule's buy requirement (discount_step).
     *
     * @param availableGifts - List of available gift data with rules
     * @param quantity - Current product quantity
     * @returns True if at least one gift rule is eligible
     */
    hasEligibleGiftsForQuantity: (availableGifts: FreeGiftsData[], quantity: number): boolean => {
        return availableGifts.some(giftData => {
            const rule = giftData.rule;
            if (!rule || !giftData.products?.length) return false;
            const buyX = rule.discount_step || 1;
            return Math.floor(quantity / buyX) > 0;
        });
    },

    /**
     * Check if there are eligible remaining gifts for subsequent rounds.
     * Considers already-selected rules and the remaining quantity after previous rounds.
     *
     * @param availableGifts - List of available gift data with rules
     * @param selectedGifts - Currently selected gifts
     * @param totalQuantity - Total product quantity
     * @returns True if at least one unselected gift rule is eligible with remaining quantity
     */
    hasEligibleRemainingGifts: (
        availableGifts: FreeGiftsData[],
        selectedGifts: ReadonlyArray<SelectedGift>,
        totalQuantity: number
    ): boolean => {
        const selectedRuleIds = new Set(selectedGifts.map(gift => gift.ruleId));

        // Calculate remaining quantity after previous rounds
        let remainingQuantity = totalQuantity;
        selectedGifts.forEach(gift => {
            remainingQuantity -= gift.quantityUsedByRule || 0;
        });

        if (remainingQuantity <= 0) return false;

        return availableGifts.some(giftData => {
            const ruleId = giftData.rule?.rule_id;
            if (selectedRuleIds.has(ruleId ?? 0)) return false;
            if (!giftData.rule || !giftData.products?.length) return false;
            const buyX = giftData.rule.discount_step || 1;
            return Math.floor(remainingQuantity / buyX) > 0;
        });
    },
} as const;

/**
 * Type for gift selection utilities
 */
export type GiftSelectionUtils = typeof giftSelectionUtils;
